/**
 * Time data as TanStack queries, all under the `["time"]` key so one
 * invalidation (a write here, an assistant time tool, an undo) refreshes the
 * timesheet, tracker chip, sidebar count, Home cards and case pane together.
 */
import { useQuery } from "@tanstack/react-query";

import { APIError } from "@/api/utils/errors";
import { queryClient } from "@/lib/query/queryClient";

import {
  ExpenseAPI,
  GuidelineAPI,
  PrebillAPI,
  TimeAdminAPI,
  TimeAPI
} from "./api/timeApi";
import { PROVIDERS } from "./review/exportModel";
import { subscribeTimeChanged } from "./timeEvents";
import type {
  ExpenseFilters,
  ExportRequest,
  GuidelineKind,
  GuidelineOwner,
  ReviewFilters,
  TimeGuideline,
  TimeStatus
} from "./types";

export const TIME_KEY = ["time"] as const;

export const timeKeys = {
  status: () => [...TIME_KEY, "status"] as const,
  entries: (from: string, to: string) =>
    [...TIME_KEY, "entries", from, to] as const,
  today: () => [...TIME_KEY, "today"] as const,
  unreleased: () => [...TIME_KEY, "unreleased"] as const,
  signals: (date: string) => [...TIME_KEY, "signals", date] as const,
  brief: (date: string | null) =>
    [...TIME_KEY, "brief", date ?? "today"] as const,
  mySettings: () => [...TIME_KEY, "me", "settings"] as const,
  caseTime: (id: string) => [...TIME_KEY, "case", id] as const,
  caseBilling: (id: string) => [...TIME_KEY, "case", id, "billing"] as const,
  caseExpenses: (id: string) => [...TIME_KEY, "case", id, "expenses"] as const,
  caseRules: (id: string) => [...TIME_KEY, "case", id, "rules"] as const,
  guidelines: (owner: GuidelineOwner, kind?: GuidelineKind) =>
    [
      ...TIME_KEY,
      "guidelines",
      owner.company_id ?? `fn:${owner.foreign_national_id}`,
      kind ?? "all"
    ] as const,
  admin: (...parts: string[]) => [...TIME_KEY, "admin", ...parts] as const
};

export const refreshTime = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: TIME_KEY });

subscribeTimeChanged(() => {
  void refreshTime();
});

/** `403 time_tracking_off` and friends read as "not available", not an error. */
export const isTimeOff = (error: unknown): boolean =>
  error instanceof APIError && (error.status === 403 || error.status === 404);

const OFF: TimeStatus = { enabled: false, is_admin: false };

/** Whether the firm has time tracking on, and whether this person is a firm admin. */
export const useTimeStatus = (enabled = true) => {
  const query = useQuery(
    {
      queryKey: timeKeys.status(),
      queryFn: () => TimeAPI.status().catch(() => OFF),
      staleTime: 5 * 60_000,
      enabled
    },
    queryClient
  );
  return { ...(query.data ?? OFF), isLoading: query.isLoading };
};

const onlyWhenOn = {
  retry: (count: number, error: unknown) => !isTimeOff(error) && count < 1
};

export const useTimeEntries = (from: string, to: string, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.entries(from, to),
      queryFn: () => TimeAPI.list(from, to),
      enabled: enabled && Boolean(from && to),
      ...onlyWhenOn
    },
    queryClient
  );

export const useTimeToday = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.today(),
      queryFn: TimeAPI.today,
      enabled,
      refetchInterval: 5 * 60_000,
      ...onlyWhenOn
    },
    queryClient
  );

export const useUnreleasedDays = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.unreleased(),
      queryFn: TimeAPI.unreleased,
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

export const useDaySignals = (date: string, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.signals(date),
      queryFn: () => TimeAPI.signals(date).then((d) => d.signals),
      enabled: enabled && Boolean(date),
      ...onlyWhenOn
    },
    queryClient
  );

export const useTimeBrief = (date: string | null, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.brief(date),
      queryFn: () => TimeAPI.brief(date ?? undefined),
      enabled,
      staleTime: 60_000,
      ...onlyWhenOn
    },
    queryClient
  );

export const useMyTimeSettings = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.mySettings(),
      queryFn: TimeAPI.mySettings,
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

export const useCaseTime = (
  individualId: string | null,
  enabled = true,
  refetchInterval: number | false = false
) =>
  useQuery(
    {
      queryKey: timeKeys.caseTime(individualId ?? ""),
      queryFn: () => TimeAPI.caseTime(individualId as string),
      enabled: enabled && Boolean(individualId),
      refetchInterval,
      ...onlyWhenOn
    },
    queryClient
  );

export const useCaseExpenses = (individualId: string | null, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.caseExpenses(individualId ?? ""),
      queryFn: () => ExpenseAPI.forCase(individualId as string),
      enabled: enabled && Boolean(individualId),
      ...onlyWhenOn
    },
    queryClient
  );

export const useCaseRules = (individualId: string | null, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.caseRules(individualId ?? ""),
      queryFn: () => GuidelineAPI.caseRules(individualId as string),
      enabled: enabled && Boolean(individualId),
      ...onlyWhenOn
    },
    queryClient
  );

const reading = (rows: TimeGuideline[] | undefined) =>
  rows?.some((g) => g.status === "extracting") ? 3_000 : false;

/** A company's or client's guideline files; polls while one is being read. */
export const useGuidelines = (
  owner: GuidelineOwner | null,
  kind?: GuidelineKind,
  enabled = true
) =>
  useQuery(
    {
      queryKey: owner
        ? timeKeys.guidelines(owner, kind)
        : [...TIME_KEY, "guidelines", "none"],
      queryFn: () => GuidelineAPI.list(owner as GuidelineOwner, kind),
      enabled: enabled && Boolean(owner),
      refetchInterval: (query) => reading(query.state.data),
      ...onlyWhenOn
    },
    queryClient
  );

/** One entry with its history; admins get no raw activity for others' time. */
export const useTimeEntry = (id: string | null) =>
  useQuery(
    {
      queryKey: [...TIME_KEY, "entry", id ?? ""],
      queryFn: () => TimeAPI.get(id as string),
      enabled: Boolean(id),
      ...onlyWhenOn
    },
    queryClient
  );

// ---- firm admins ----------------------------------------------------------

export const useFirmTimeSettings = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("settings"),
      queryFn: TimeAdminAPI.settings,
      enabled,
      retry: false
    },
    queryClient
  );

export const useTimekeepers = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("timekeepers"),
      queryFn: TimeAdminAPI.timekeepers,
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

export const useReviewEntries = (filters: ReviewFilters, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("entries", JSON.stringify(filters)),
      queryFn: () => TimeAdminAPI.entries(filters),
      enabled,
      placeholderData: (previous) => previous,
      ...onlyWhenOn
    },
    queryClient
  );

export const useReviewExpenses = (filters: ExpenseFilters, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("expenses", JSON.stringify(filters)),
      queryFn: () => ExpenseAPI.review(filters),
      enabled,
      placeholderData: (previous) => previous,
      ...onlyWhenOn
    },
    queryClient
  );

export const useReviewKpis = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("kpis"),
      queryFn: TimeAdminAPI.kpis,
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

export const useProviders = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("providers"),
      queryFn: TimeAdminAPI.providers,
      placeholderData: [...PROVIDERS],
      staleTime: 5 * 60_000,
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

export const useExports = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("exports"),
      queryFn: TimeAdminAPI.exports,
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

/** Re-runs whenever time changes, so a fixed Billing ID clears its issue. */
export const useExportPreview = (req: ExportRequest, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("export-preview", JSON.stringify(req)),
      queryFn: () => TimeAdminAPI.previewExport(req),
      placeholderData: (prev) => prev,
      enabled,
      retry: false
    },
    queryClient
  );

type Range = { from?: string; to?: string };

export const usePrebill = (range: Range, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("prebill", range.from ?? "", range.to ?? ""),
      queryFn: () => PrebillAPI.matters(range),
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

export const usePrebillMatter = (individualId: string | null) =>
  useQuery(
    {
      queryKey: timeKeys.admin("prebill", "matter", individualId ?? ""),
      queryFn: () => PrebillAPI.matter(individualId ?? ""),
      enabled: Boolean(individualId),
      ...onlyWhenOn
    },
    queryClient
  );

export const useWaiting = (range: Range, enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("waiting", range.from ?? "", range.to ?? ""),
      queryFn: () => TimeAdminAPI.waiting(range.from, range.to),
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );

export const useMatters = (enabled = true) =>
  useQuery(
    {
      queryKey: timeKeys.admin("matters"),
      queryFn: TimeAdminAPI.matters,
      enabled,
      ...onlyWhenOn
    },
    queryClient
  );
